import { useAuth } from '@/context/AuthContext';
import { useEntityList } from '@/hooks/useEntityList';
import { useEntityOperations } from '@/hooks/useEntityOperations';
import { StoreProduct } from '@/types/entities';
import { DbStoreProduct } from '@/types/entities/store-product';
import { Database } from '@/types/supabase';
import { 
  mapDbStoreProductToStoreProduct, 
  mapStoreProductToDbInsert, 
  mapStoreProductUpdateToDbUpdate 
} from '@/shared/mappers/storeProductMappers';

// Используем централизованные мапперы вместо локальных функций

export const useStoreProductsUnified = (filters?: Record<string, unknown>) => {
  const { user } = useAuth();
  
  // Wrapper для маппера с автоматической передачей текущего пользователя
  const mapStoreProductToDbInsertWithUser = (product: Partial<StoreProduct>) => {
    return mapStoreProductToDbInsert(product, user?.id);
  };
  
  // Обработка rentalPointId фильтра (camelCase -> snake_case маппинг)
  const processedFilters: Record<string, unknown> = { ...(filters ?? {}) };
  if (filters && Object.prototype.hasOwnProperty.call(filters, 'rentalPointId')) {
    const rentalPointId = (filters as Record<string, unknown>)['rentalPointId'];
    processedFilters['rental_point_id'] = rentalPointId;
    delete (processedFilters as Record<string, unknown>)['rentalPointId'];
  }
  
  const queryKey: (string | Record<string, unknown> | undefined)[] = [
    'store_products',
    { userId: user?.id, ...processedFilters } as Record<string, unknown>,
  ];

  const { 
    data: products, 
    isLoading, 
    error, 
    refetch 
  } = useEntityList<StoreProduct, 'store_products'> ({
    tableName: 'store_products',
    queryKey,
    dbEntityToEntityMapper: mapDbStoreProductToStoreProduct,
    filters: { user_id: user?.id, ...processedFilters },
  });
  
  const {
    createEntity: createProduct,
    updateEntity: updateProduct,
    deleteEntity: deleteProduct,
    isCreating,
    isUpdating,
    isDeleting,
  } = useEntityOperations<StoreProduct, 'store_products'> ({
    tableName: 'store_products',
    queryKey,
    dbEntityToEntityMapper: (mapDbStoreProductToStoreProduct as unknown as (
      db: Database['public']['Tables']['store_products']['Row']
    ) => StoreProduct),
    entityToInsertDbMapper: mapStoreProductToDbInsertWithUser,
    entityToUpdateDbMapper: mapStoreProductUpdateToDbUpdate,
  });

  return {
    products: products || [],
    isLoading,
    error,
    refetch,
    createProduct,
    updateProduct,
    deleteProduct,
    isCreating,
    isUpdating,
    isDeleting,
  };
};
